import React from 'react';
import { connect } from 'react-redux';

import { DEFAULT_LANGUAGE, normalizeLanguage, userStorageKey } from './languages';
import { getSavedLanguage, setSavedLanguage } from './storage';
import { translate } from './translate';

function createTranslator(language) {
    return (key, vars) => translate(language, key, vars);
}

export const LanguageContext = React.createContext({
    language: DEFAULT_LANGUAGE,
    setLanguage: () => {},
    t: createTranslator(DEFAULT_LANGUAGE),
});

export class LanguageProvider extends React.Component {
    constructor(props) {
        super(props);
        const userKey = userStorageKey(props.user);
        this.state = {
            language: getSavedLanguage(userKey) || DEFAULT_LANGUAGE,
            userKey,
        };
    }

    componentDidMount() {
        this.syncDocumentLang();
    }

    componentDidUpdate(prevProps, prevState) {
        this.handleUserKeyChange(prevProps);
        if (prevState.language !== this.state.language) {
            this.syncDocumentLang();
        }
    }

    handleUserKeyChange(prevProps) {
        const nextKey = userStorageKey(this.props.user);
        if (nextKey === this.state.userKey && prevProps.user === this.props.user) {
            return;
        }
        if (nextKey === this.state.userKey) {
            return;
        }

        const saved = getSavedLanguage(nextKey);
        let language = saved || this.state.language || DEFAULT_LANGUAGE;

        if (!saved && this.state.userKey === 'anon' && nextKey !== 'anon') {
            const anonLanguage = getSavedLanguage('anon') || this.state.language;
            if (anonLanguage) {
                setSavedLanguage(nextKey, anonLanguage);
                language = anonLanguage;
            }
        }

        this.setState({
            language: normalizeLanguage(language) || DEFAULT_LANGUAGE,
            userKey: nextKey,
        });
    }

    syncDocumentLang() {
        if (typeof document !== 'undefined' && document.documentElement) {
            document.documentElement.lang = this.state.language;
        }
    }

    setLanguage = (code) => {
        const language = setSavedLanguage(this.state.userKey, code);
        this.setState({ language }, this.syncDocumentLang);
    };

    render() {
        const value = {
            language: this.state.language,
            setLanguage: this.setLanguage,
            t: createTranslator(this.state.language),
        };

        return (
            <LanguageContext.Provider value={value}>
                {this.props.children}
            </LanguageContext.Provider>
        );
    }
}

function LanguageGateView({ user, children }) {
    return (
        <LanguageProvider user={user}>
            {children}
        </LanguageProvider>
    );
}

export const LanguageGate = connect((state) => ({
    user: state.auth.currentUser,
}))(LanguageGateView);

export function withTranslation(Component) {
    class TranslatedComponent extends React.Component {
        render() {
            return (
                <LanguageContext.Consumer>
                    {(ctx) => (
                        <Component
                            {...this.props}
                            t={(ctx && ctx.t) || createTranslator(DEFAULT_LANGUAGE)}
                            language={(ctx && ctx.language) || DEFAULT_LANGUAGE}
                            setLanguage={(ctx && ctx.setLanguage) || (() => {})}
                        />
                    )}
                </LanguageContext.Consumer>
            );
        }
    }

    const name = Component.displayName || Component.name || 'Component';
    TranslatedComponent.displayName = `withTranslation(${name})`;
    if (typeof Component.isAuthenticated === 'function') {
        TranslatedComponent.isAuthenticated = Component.isAuthenticated;
    }
    return TranslatedComponent;
}

export function useLanguage() {
    return React.useContext(LanguageContext);
}
